import { FinancialStatementAccount } from '../../models/common/FinancialStatementAccount';
import { FinancialStatementPart } from '../../models/common/FinancialStatementPart';
import { GeneralLedgerTransaction } from '../../models/common/GeneralLedgerTransaction';
import { FinancialStatementItem } from '../../models/common/FinancialStatementItem';
import { ButtonProps } from '../../../shared/component/buttons/button.component';
import { FinancialStatement } from '../../models/common/FinancialStatement';
import { PlExcelExportData } from 'src/app/domain/excel/templates/financial-statement/profit-and-loss/profitAndLossTemplate';
import { BsExcelExportData } from 'src/app/services/balance-sheet/bsTemplate';
import { CapitalAccountData } from '../capital-account/utils';
import { FS_URLS } from '../../module/financial-statement-routing.module';
import { Fund } from '../../../fund-performance/dto/Fund';
import { ManagementEntity } from '../../../organization/dto/ManagementEntity';
import { FsSelectorPeriod } from './FsSelectorPeriod';
import { ModalButtons } from '../../../shared/component/modal-container/modal-container.component';

export const baseFinancialStatementItems = {
  assetsItems: ['cash', 'portfolio investments', 'portfolio appreciation', 'accounts receivable'],
  liabilitiesItems: ['contribution in advance', 'commitment to investments', 'accounts payable', 'loan'],
  partnersCapitalItems: [
    'contribution', 'due to capital contribution', 'contribution received in advance', 'distribution',
    'tax distribution', 'late payment interest', 'accumulated profit & loss',
  ],

  assetLiabilityItems: ['related parties'],
  profitsItems: ['realized gain', 'unrealized gain'],
  lossesItems: [
    'management fees', 'general and administrative', 'organizational expenses', 'financial expenses', 'other expenses',
  ],
};

export const accumulatedProfitAndLoss: string = baseFinancialStatementItems.partnersCapitalItems.at(-1);

export const fixedItemNames = baseFinancialStatementItems.partnersCapitalItems.slice(0, 5);

export interface CheckboxState {
  selected: boolean;
  minus: boolean;
}

export class FsStatusPeriodDTO {
  period: Date;
  isCompleted: boolean;
}

export interface ManualEntryObjectForEmitter {
  action: EmitterFlags;
  manualEntry: GeneralLedgerTransaction;
}

export interface ManualAccountObjectForEmitter {
  action: EmitterFlags;
  manualAccount: FinancialStatementAccount;
}

export interface ColumnDate {
  selectedDate: string;
  minDate: string;
  maxDate: string;
}

export interface PeriodWithStartAndEnd {
  start: string;
  end: string;
}

export class ShownPeriodsData {
  quarters: string[] = [];
  years: string[] = [];
  quarterHeaders?: PeriodWithStartAndEnd[];
  yearHeaders?: PeriodWithStartAndEnd[];
}

export const getAllExistingPeriodsFromParts = (financialStatementParts: FinancialStatementPart[]): Set<string> =>
  new Set(financialStatementParts.flatMap((part) => Array.from(part.sums.quarterlySums.keys())));

/** emitter object for pl allocation to accumulated PL item */
export interface PlAllocationEmissionObject {
  selectedAccounts: FinancialStatementAccount[];
  item: FinancialStatementItem;
}

export class AccountFilterData {
  selectorPeriods: Date[];
  selectedPeriod: Date;
  selectedDate: Date;
  constructor(copyData: AccountFilterData) {
    this.selectedDate = copyData.selectedDate;
    this.selectedPeriod = copyData.selectedPeriod;
    this.selectorPeriods = copyData.selectorPeriods;
  }
}

export interface EmptyStateSettings {
  text: UtilityTexts;
  buttonSettings: ButtonProps;
}

export interface ItemNameAndStatus {
  name: string;
  isAssetsAndLiabilities: boolean;
}

export enum FsExcelExportType {
  FS,
  ASSEMBLY,
  GL,
  TB,
  MANUAL_ENTRIES,
  MANUAL_ADJUSTMENTS,
  MANUAL_ACCOUNTS,
  QUARTERLY_GL,
  BS,
  PL
}

export const enum PeriodType {
  quarter = 'quarter',
  year = 'year',
}

export enum EmitterFlags {
  EDIT,
  DELETE,
  CONVERT,
  DUPLICATE,
}

export class UtilityTexts {
  title: string;
  description: string;
}

export const TOTAL_CHECK_FAILED_TEXT: UtilityTexts = {
  title: 'Total check failed.',
  description: 'Please recheck that all balance items are allocated properly.',
};

export const PERIOD_SELECT_MODAL_TEXT: UtilityTexts = {
  title: 'Copy structure',
  description: 'Copy structure from:',
};

/**
 * Determines and returns the appropriate array of FS_TABs based on the provided URL and the `isFund` flag.
 *
 * @param {string} url - The URL used to determine which tabs to return.
 * @param {boolean} isFund - Indicates if the entity is a fund or management entity, affecting the inclusion of SOI.
 * @returns {FS_TAB[]} An array of FS_TABs corresponding to the URL and `isFund` flag.
 */
export const getFsTabsByUrl = (url: string, isFund: boolean): FS_TAB[] => {
  if (url.includes(FS_URLS._workingPaper)) {
    if (isFund) {
      return WorkingPaperTabs as FS_TAB[];
    }
    return ManagementEntityWpTabs as FS_TAB[];
  } if (url.includes(FS_URLS._financialsAnalytics)) {
    return FinancialAnalyticsTabs as FS_TAB[];
  } if (url.includes(FS_URLS._capAccounts)) {
    return CapitalAccountsTabs as FS_TAB[];
  }
};

export const getFsUrlForTab = (tab: FS_TAB): string => {
  const url = tabToUrlMap[tab];
  if (url) {
    return url;
  }
  return '';

};

export class FsNavData {
  isCompleted = false;
  entities: (Fund | ManagementEntity)[];
  selectedEntity: Fund | ManagementEntity;
  periods: FsSelectorPeriod[];
  selectedPeriod: FsSelectorPeriod;
}

export enum WORKING_PAPERS_TABS {
  GL = 'General ledger',
  TB = 'Accounts summary',
  ASSEMBLY = 'Assembly',
  SOI = 'Schedule of investments',
}

export enum MANAGEMENT_ENTITY_WP_TABS {
  GL = 'General ledger',
  TB = 'Accounts summary',
  ASSEMBLY = 'Assembly',
}

export enum FINANCIALS_ANALYTICS_TABS {
  BS = 'Balance sheet',
  PL = 'Profit and loss',
}

export enum CAPITAL_ACCOUNTS_TABS {
  CA = 'Capital accounts',
}

export enum ADJUSTMENTS_TABS {
  ME = 'Manual entries',
  MA = 'Created accounts'
}


export const WorkingPaperTabs = Object.values(WORKING_PAPERS_TABS);
export const ManagementEntityWpTabs = Object.values(MANAGEMENT_ENTITY_WP_TABS);
export const FinancialAnalyticsTabs = Object.values(FINANCIALS_ANALYTICS_TABS);
export const CapitalAccountsTabs = Object.values(CAPITAL_ACCOUNTS_TABS);
export const AdjustmentsTabs = Object.values(ADJUSTMENTS_TABS);


// Extract union types from enums
export type WorkingPaperTab = WORKING_PAPERS_TABS[keyof WORKING_PAPERS_TABS];
export type ManagementEntityWpTab = MANAGEMENT_ENTITY_WP_TABS[keyof MANAGEMENT_ENTITY_WP_TABS];
export type FinancialAnalyticsTab = FINANCIALS_ANALYTICS_TABS[keyof FINANCIALS_ANALYTICS_TABS];
export type CapitalAccountsTab = CAPITAL_ACCOUNTS_TABS[keyof CAPITAL_ACCOUNTS_TABS];
export type AdjustmentsTab = ADJUSTMENTS_TABS[keyof ADJUSTMENTS_TABS];


// Union of all tab types
export type FS_TAB = FS_TABS[number];

export type FS_TABS = WORKING_PAPERS_TABS | MANAGEMENT_ENTITY_WP_TABS | FINANCIALS_ANALYTICS_TABS | CAPITAL_ACCOUNTS_TABS | ADJUSTMENTS_TABS;

const tabToUrlMap: { [key in FS_TAB]: string } = {
  // WORKING_PAPERS_TABS
  [WORKING_PAPERS_TABS.GL]: `${FS_URLS._workingPaper}/${FS_URLS._gl}`,
  [WORKING_PAPERS_TABS.TB]: `${FS_URLS._workingPaper}/${FS_URLS._tb}`,
  [WORKING_PAPERS_TABS.ASSEMBLY]: `${FS_URLS._workingPaper}/${FS_URLS._assembly}`,
  [WORKING_PAPERS_TABS.SOI]: `${FS_URLS._workingPaper}/${FS_URLS._soi}`,

  // FINANCIALS_ANALYTICS_TABS
  [FINANCIALS_ANALYTICS_TABS.BS]: `${FS_URLS._financialsAnalytics}/${FS_URLS._bs}`,
  [FINANCIALS_ANALYTICS_TABS.PL]: `${FS_URLS._financialsAnalytics}/${FS_URLS._pl}`,

  // CAPITAL_ACCOUNTS_TABS
  [CAPITAL_ACCOUNTS_TABS.CA]: `${FS_URLS._capAccounts}/${FS_URLS._capAccounts}`,
};

export const BALANCE_SHEET_PARTS = ['Assets', 'Liabilities', 'Partners capital'];

export const PROFIT_LOSS_PARTS = ['Profits', 'Losses'];

export interface FsExportExcelData {
  fs: FinancialStatement;
  capitalAccountData: CapitalAccountData;
  bsPageData: BsExcelExportData;
  plPageData: PlExcelExportData;
}

export enum FsStatus {
  none = 'none',
  started = 'started',
  completed = 'completed',
}

export const CloseApplyDismissModalButtons: ModalButtons = {
  close: true,
  apply: 'Apply',
  dismiss: 'Dismiss',
};

export const CloseApplyCancelModalButtons: ModalButtons = {
  close: true,
  apply: 'Apply',
  dismiss: 'Cancel',
};

export const CopyAdjustmentWarningTexts: UtilityTexts = {
  title: 'Some entries are hidden',
  description: 'They are dated after the current period',
};

export class ShowFs {
  modalForManualEntry = false;
  addAccountModal = false;
  deleteModal = false;
  thinkTwiceModalForStructure = false;
  copyStructureModal = false;
  createPeriodModal = false;
  copyAdjustmentsModal = false;
}

export enum OwnershipEntityType {
  FUND = 'FUND',
  MANAGEMENT_ENTITY = 'MANAGEMENT_ENTITY',
}
